import { useContext, useState, useEffect } from 'react';
import {
  Box,
  alpha,
  lighten,
  IconButton,
  Tooltip,
  styled,
  useTheme
} from '@mui/material';
import MenuTwoToneIcon from '@mui/icons-material/MenuTwoTone';
import { SidebarContext } from 'src/contexts/SidebarContext';
import CloseTwoToneIcon from '@mui/icons-material/CloseTwoTone';

import HeaderUserbox from './Userbox';

const HeaderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isShowed' && prop !== 'isScrolled'
})<{ isShowed: boolean; isScrolled: boolean }>(
  ({ theme, isShowed, isScrolled }) => `
        height: ${theme.header.height};
        color: ${theme.header.textColor};
        padding: ${theme.spacing(0, 2)};
        z-index: 6;
        background-color: #fff; 
        position: fixed;
        top: ${isScrolled ? '0' : '16px'};
        margin: ${isScrolled ? '32px' : '16px'};
        margin-top: 0;
        left: ${
          isShowed
            ? isScrolled
              ? `${theme.sidebar.width}`
              : `calc(${theme.sidebar.width} + ${theme.spacing(2)})`
            : isScrolled
            ? '0'
            : theme.spacing(2)
        };
        right: ${isScrolled ? '0' : theme.spacing(2)};
        width: auto;
        border-radius: ${isScrolled ? '0 0 20px 20px' : '20px'};
        display: flex;
        align-items: center;
        justify-content: space-between;
        box-shadow: 0 4px 20px rgba(0,0,0,0.08);
        background-color: ${alpha('#fff', 0.85)};
        backdrop-filter: blur(8px);
        transition: all 0.3s ease-in-out;
        
        @media (max-width: ${theme.breakpoints.values.lg}px) {
          left: ${isScrolled ? '0' : theme.spacing(2)};
          right: ${isScrolled ? '0' : theme.spacing(2)};
          margin: ${isScrolled ? '0' : theme.spacing(2)};
          margin-top: 0;
        }
`
);

function Header({ callback, isShowed }) {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const theme = useTheme();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <HeaderWrapper
      sx={{
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 1px 0 ${alpha(
                lighten(theme.colors.primary.main, 0.7),
                0.15
              )}, 0px 2px 8px -3px rgba(0, 0, 0, 0.2), 0px 5px 22px -4px rgba(0, 0, 0, .1)`
            : `0px 2px 8px -3px ${alpha(
                theme.colors.alpha.black[100],
                0.2
              )}, 0px 5px 22px -4px ${alpha(
                theme.colors.alpha.black[100],
                0.1
              )}`
      }}
      isShowed={isShowed}
      isScrolled={isScrolled}
    >
      <Box>
        <IconButton
          color="primary"
          onClick={() => callback(!isShowed)}
          sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
        >
          <MenuTwoToneIcon fontSize="small" />
        </IconButton>

        <IconButton
          color="primary"
          onClick={toggleSidebar}
          sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
        >
          {!sidebarToggle ? (
            <MenuTwoToneIcon fontSize="small" />
          ) : (
            <CloseTwoToneIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      <Box display="flex" alignItems="center">
        <HeaderUserbox />
      </Box>
    </HeaderWrapper>
  );
}

export default Header;
